import { useState } from 'react';
import moment from 'moment';
import ClinicFilter from './ClinicFilter/ClinicFilter';
import Table from './Table/Table';
import AppointmentStatus from 'components/AppontmentStatus/AppointmentStatus';
import { IAppointmentStatus } from 'hooks/useAppointment';
import {
  CALENDAR_TYPE,
  getDateRangeByCalendarType,
} from 'utils/getDateRangeByCalendarType';
import AppointmentDateRange from 'components/AppointmentDateRange/AppointmentDateRange';
import axiosInstance from 'apis/axiosInstance';

import { ROLE } from 'utils/constants';
import { loadRole } from 'utils/storage';

const defaultTableSortState = {
  sortBy: 'appointmentDate',
  sort: 'desc' as const,
};

const AdminAppointmentPage = () => {
  const role = loadRole();
  const isAdminRole = [ROLE.GLOBAL_ADMIN, ROLE.GROUP_ADMIN].includes(role);

  const [selectedClinicIds, setSelectedClinicIds] = useState<string[]>([]);

  const [selectedStatus, setSelectedStatus] = useState<IAppointmentStatus>(
    isAdminRole ? 'all' : 'new'
  );

  const [shouldRun, setShouldRun] = useState(true);

  const [selectedDateRange, setSelectedDateRange] = useState<{
    fromDate: string;
    toDate: string;
  }>(
    () =>
      getDateRangeByCalendarType(CALENDAR_TYPE.THIS_MONTH) as {
        fromDate: string;
        toDate: string;
      }
  );

  const [sort, setSort] = useState<'asc' | 'desc'>(defaultTableSortState.sort);

  const [sortBy, setSortBy] = useState(defaultTableSortState.sortBy);

  const [selectedCalendarType, setSelectedCalendarType] = useState(
    CALENDAR_TYPE.THIS_MONTH
  );

  const onChangeDateRange = (dateRange: {
    fromDate: string;
    toDate: string;
  }) => {
    setSelectedDateRange(dateRange);
  };

  const onChangeClinic = async (clinicIds: string[]) => {
    setShouldRun(false);
    if (selectedCalendarType === CALENDAR_TYPE.ALL_TIME) {
      try {
        const rs = await axiosInstance.get('/appointments/date-range', {
          params: { clinicIds: clinicIds },
        });
        onChangeDateRange({
          fromDate: moment
            .utc(rs.data.earliestDateTime ?? selectedDateRange.fromDate)
            .format('YYYY-MM-DD'),
          toDate: moment
            .utc(rs.data.latestDateTime ?? selectedDateRange.toDate)
            .format('YYYY-MM-DD'),
        });
      } catch (error) {}
    }
    setSelectedClinicIds(clinicIds);
    setShouldRun(true);
  };

  return (
    <div
      className="pt-2.4 pl-1.8 pr-4 text-14 leading-[2.1rem] text-darkest-grey pb-12 relative"
      data-testid="page-admin-appointment"
    >
      <div className="flex flex-row justify-between">
        <h3 className="text-24 font-bold leading-[3.6rem]">Appointments</h3>
      </div>

      <div className="my-2.4 flex gap-x-1.6 items-center">
        <AppointmentStatus
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          isShownAllOption={isAdminRole}
        />
        <ClinicFilter onSelect={onChangeClinic} />

        <div className="flex-1 text-right">
          <AppointmentDateRange
            selectedCalendarType={selectedCalendarType}
            setSelectedCalendarType={setSelectedCalendarType}
            clinicIds={selectedClinicIds}
            dateRange={selectedDateRange}
            onChangeDateRange={onChangeDateRange}
          />
        </div>
      </div>

      <div className="bg-white shadow-primary rounded-[1rem]">
        <Table
          key={`${selectedStatus}${selectedDateRange.fromDate}${
            selectedDateRange.toDate
          }${sort}${sortBy}${selectedClinicIds.join(',')}`}
          clinicIds={selectedClinicIds}
          status={selectedStatus}
          dateRange={selectedDateRange}
          sort={sort}
          setSort={setSort}
          sortBy={sortBy}
          setSortBy={setSortBy}
          shouldRun={shouldRun}
        />
      </div>
    </div>
  );
};

export default AdminAppointmentPage;
